import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { CssBaseline, ThemeProvider, createTheme, CircularProgress, Box, Typography, Button } from '@mui/material';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import StudyList from './components/StudyList';
import ViewerPage from './components/ViewerPage';
import AdminPage from './components/admin/AdminPage';
import UserProfileSettings from './components/UserProfileSettings';
import { ScrollSyncProvider } from './contexts/ScrollSyncContext';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0d0d0d', paper: '#1a1a1a' },
    primary: { main: '#00bcd4' },
    text: { primary: '#e0e0e0', secondary: '#888' },
  },
  typography: { fontFamily: 'monospace' },
});

// ─── Error Boundary ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  componentDidCatch(error, info) {
    this.setState({ error, info });
    console.error('GQG ErrorBoundary caught:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <Box sx={{ height: '100vh', bgcolor: '#0a0a0a', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
          <Typography sx={{ color: '#f44336', fontSize: 14, fontWeight: 'bold', letterSpacing: 2 }}>
            ◈ RENDER ERROR
          </Typography>
          <Box sx={{ bgcolor: '#1a0000', border: '1px solid #f44336', borderRadius: 1, p: 2, maxWidth: 800, width: '100%' }}>
            <Typography sx={{ color: '#ff5252', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {this.state.error?.toString()}
            </Typography>
            {this.state.info?.componentStack && (
              <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: 10, whiteSpace: 'pre-wrap',
                wordBreak: 'break-all', mt: 1, maxHeight: 300, overflow: 'auto' }}>
                {this.state.info.componentStack}
              </Typography>
            )}
          </Box>
          <Button variant="outlined" size="small" onClick={() => window.location.href = '/'}
            sx={{ color: '#00bcd4', borderColor: '#00bcd4', fontSize: 10 }}>
            ← Back to Study List
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// ─── Protected route wrapper ──────────────────────────────────────────────────
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Session restore in progress — show spinner rather than flashing login
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#0a0a0a' }}>
        <CircularProgress size={24} sx={{ color: '#00bcd4' }} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route path="/" element={
        <RequireAuth><StudyList /></RequireAuth>
      } />

      <Route path="/study/:studyInstanceUID" element={
        <ErrorBoundary>
          <RequireAuth><ScrollSyncProvider><ViewerPage /></ScrollSyncProvider></RequireAuth>
        </ErrorBoundary>
      } />

      <Route path="/admin/*" element={
        <RequireAuth><AdminPage /></RequireAuth>
      } />

      <Route path="/settings" element={
        <RequireAuth><UserProfileSettings /></RequireAuth>
      } />

      {/* Catch-all → home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <DndProvider backend={HTML5Backend}>
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </DndProvider>
    </ThemeProvider>
  );
}
